import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import MoleculeTooltip from "components/source/molecules/tooltip/molecule-tooltip";
import RecommendationText from "components/source/membership/fit/recommendation-text";
import ActionLogger from "action-logger";
import MembershipHelpers, { isSubscriptionMember } from "helpers/membership-helpers";
import { TOOLTIP } from "rtr-constants";
import FitRecommendationTooltip from "components/source/membership/fit/fit-recommendation-tooltip";
import { userDataPropType } from "components/propTypes";
import { displayModal } from "actions/shared-actions";
import FitInformationModal from "components/source/new_taxonomy/fit-information-modal.jsx";
import happinessSurveyActions from "actions/happiness-survey-actions";
import PreviouslyRentedSize from "components/source/membership/fit/previously-rented-size";
import Tooltip from "images/sprites/reb/svg/question-mark.svg";
import RecommendedFitTag from "components/source/new_taxonomy/recommended-fit-tag";
import { fetchfitRates } from "../../../../slices/fitRate-slice";

class FitRecommendation extends React.Component {
  static propTypes = {
    membershipId: PropTypes.number,
    membershipType: PropTypes.string,
    primarySize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    product: PropTypes.object,
    recommendations: PropTypes.arrayOf(
      PropTypes.shape({
        size: PropTypes.string,
        percentage: PropTypes.number,
        surveyCount: PropTypes.number,
        isRecommended: PropTypes.bool,
      })
    ),
    recommendedSizeAvail: PropTypes.bool,
    style: PropTypes.string,
    userData: userDataPropType,
    userId: PropTypes.string,
    dispatchFetchFitRates: PropTypes.func.isRequired,
    fetchHappinessSurveysByStyle: PropTypes.func.isRequired,
    surveys: PropTypes.arrayOf(
      PropTypes.shape({
        size: PropTypes.string.isRequired,
        surveyFeedbacks: PropTypes.arrayOf(PropTypes.shape({ feedback: PropTypes.string })).isRequired,
        created: PropTypes.string.isRequired,
      })
    ),
    surveysLoading: PropTypes.bool.isRequired,
  };

  state = {
    isTooltipOpen: false,
  };

  componentDidMount() {
    const { product = {}, fetchHappinessSurveysByStyle, userId } = this.props;
    const productId = product?.id || product?.productId;

    if (productId && userId) {
      fetchHappinessSurveysByStyle(productId, userId);
      if (!product?.fitRates) {
        this.fetchProductFitData();
      }
    }
  }

  componentDidUpdate(prevProps) {
    const { product = {}, fetchHappinessSurveysByStyle, userId } = this.props;
    const productId = product?.id || product?.productId;
    const prevProductId = prevProps?.product?.id || prevProps?.product?.productId;

    const isNewProduct = productId !== prevProductId;
    const isNewUser = userId !== prevProps?.userId;

    // to account for changes in product drawer
    if ((isNewProduct && userId) || (isNewUser && productId)) {
      fetchHappinessSurveysByStyle(productId, userId);
      if (!product?.fitRates) {
        this.fetchProductFitData();
      }
    }
  }

  fetchProductFitData = () => {
    const { userId, product, primarySize, dispatchFetchFitRates } = this.props;
    const productId = product?.id || product?.productId;

    if (!userId || !productId || !primarySize) {
      return;
    }

    return dispatchFetchFitRates(productId, primarySize, userId);
  };

  logTooltipOpen() {
    const { recommendedSizeAvail, recommendations } = this.props;

    if (!recommendations || recommendations.length < 1) {
      return;
    }

    const visibleRecs = recommendations.filter(s => s.isRecommended);
    const loggableRecs = visibleRecs.map(s => s.size);
    const percentRecs = visibleRecs.map(s => s.percentage);
    const recommended_sizes_obj = {};
    loggableRecs.forEach((size, index) => {
      recommended_sizes_obj[`recommended_sizes[${index}]`] = size;
    });

    ActionLogger.logAction({
      object_type: "fit2_rec",
      action: "display_rec_stats",
      membership_type: this.props.membershipType,
      membership_id: this.props.membershipId,
      user_id: this.props.userId,
      styleId: this.props.style,
      primary_size: this.props.primarySize,
      ...recommended_sizes_obj,
      percentage_happy: percentRecs.toString(),
      is_recommended_size_available: recommendedSizeAvail,
      location: "selector",
      case: "Min 1 - Max 2 with Data",
    });
  }

  handleTooltipToggle = isOpen => {
    if (isOpen !== this.state.isTooltipOpen) {
      if (!this.state.isTooltipOpen) {
        this.logTooltipOpen();
      }

      this.setState({ isTooltipOpen: isOpen });
    }
  };

  handleTooltipClose = () => {
    this.setState({ isTooltipOpen: false });
  };

  logInferAction = () => {
    ActionLogger.inferAction({
      action: "edit_primary_size",
      membership_type: this.props.membershipType,
      user_id: this.props.userId,
    });
  };

  setRefElement = element => {
    this.$target = element;
  };

  getRecommendationType = percentage => {
    if (percentage > 89) {
      return "great-fit";
    } else if (percentage > 74) {
      return "good-fit";
    } else {
      return "";
    }
  };

  isPreviouslyRentedRecommended = recommendedList => {
    const { surveys } = this.props;

    const previouslyRentedSize = PreviouslyRentedSize(surveys)?.size;

    return (
      previouslyRentedSize && !!recommendedList.find(item => item.size === previouslyRentedSize && item.isRecommended)
    );
  };

  renderBasicMessage = copy => {
    return (
      <div className="fit-recommendation bordered basic">
        <p className="universal-small--secondary">{copy}</p>
      </div>
    );
  };

  renderRecommendedFitContent = (previouslyRentedCopy, recommendations, recommendedList) => {
    const { product: { sizeScale: { sizeNames = {} } = {} } = {}, primarySize } = this.props;
    const { isTooltipOpen } = this.state;

    return (
      <div data-test-id="fit-recommendation" className="my-4" aria-label="Fit Recommendation">
        <div className="flex justify-between items-center gap-8">
          <div className="flex items-center">
            {
              // If previously rented, show relevant text instead of fit suggestion
              previouslyRentedCopy ? (
                <p className="text-sm">{previouslyRentedCopy}</p>
              ) : (
                <RecommendationText recommendedSizes={recommendedList.map(s => s.size)} />
              )
            }

            <button
              data-test-id="fit-rec-tooltip"
              className="px-1 flex"
              ref={this.setRefElement}
              onClick={this.handleTooltipToggle}
              type="button">
              <Tooltip className="pointer-events-none text-darkGray" />
            </button>

            <MoleculeTooltip
              target={this.$target}
              isOpen={isTooltipOpen}
              placement={TOOLTIP.PLACEMENTS.TOP}
              mode={TOOLTIP.MODE.CLICK}
              closeOnScroll
              onToggle={this.handleTooltipToggle}
              onClose={this.handleTooltipClose}
              fluid={true}
              closeOnOutsideClick={true}
              className={"shadow-lg z-max"}
              modifiers={{
                preventOverflow: {
                  boundariesElement: window.document.body,
                  padding: 10,
                },
              }}>
              <FitRecommendationTooltip
                primarySize={primarySize}
                recommendations={recommendations}
                sizeNames={sizeNames}
                getRecommendationType={this.getRecommendationType}
                logInferAction={this.logInferAction}
              />
            </MoleculeTooltip>
          </div>
          <RecommendedFitTag fitRates={recommendations} userData={this.props.userData} className="ml-2" />
        </div>
      </div>
    );
  };

  render() {
    const { recommendations = [], userData, surveys, surveysLoading, product, primarySize } = this.props;
    if (!isSubscriptionMember(userData)) {
      return null;
    }

    // If this is an accessory or one-size item, don't show recommendations or previous size selections
    if (product?.skus?.length < 2) {
      return null;
    }

    if (surveysLoading) {
      return null;
    }

    if (!(primarySize ?? false)) {
      return null;
    }

    const previouslyRentedCopy = PreviouslyRentedSize(surveys)?.copy;

    // If there are no fit rates at all, show previous size selection if avialable or a gathering data msg
    if (!recommendations || !recommendations.length) {
      if (previouslyRentedCopy) {
        return this.renderBasicMessage(previouslyRentedCopy);
      }

      return null;
    }

    // If recommendations are returned, check if at least one isRecommended
    const recommendedList = recommendations.filter(s => s.isRecommended);

    // If none are recommended, all suggestions have percentages too low for us to bother
    // showing this recommendation experience, show previous size selection if available
    if (!recommendedList.length) {
      if (previouslyRentedCopy) {
        return this.renderBasicMessage(previouslyRentedCopy);
      }

      return null;
    }

    return this.renderRecommendedFitContent(previouslyRentedCopy, recommendations, recommendedList);
  }
}

const mapStateToProps = state => {
  const { userData } = state;
  const primarySize = userData?.userProfile?.profiles?.primarySize;
  const userId = userData?.userProfile?.id;
  const membershipState = MembershipHelpers.getMembershipState(state);
  const membershipType = membershipState?.membershipType;
  const membershipId = membershipState?.membershipId;
  const surveys = state?.happinessSurvey?.completedSurveysByStyle;
  const surveysLoading = state?.happinessSurvey?.completedSurveysByStyleLoading ?? true;
  const recommendations = state?.pdp?.fitRates || state?.product?.fitRates || [];

  // handle for both PDP and product drawer
  const style = state?.product?.id || state?.productDrawer?.productId;

  return {
    primarySize,
    userId,
    membershipType,
    membershipId,
    style,
    userData,
    recommendations,
    surveys,
    surveysLoading,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    onDisplayModal: () => dispatch(displayModal(FitInformationModal.MODAL_NAME)),
    fetchHappinessSurveysByStyle: (styleName, userId) => {
      dispatch(happinessSurveyActions.getHappinessSurveysByStyle(styleName, userId));
    },
    dispatchFetchFitRates: (styleNames, primarySize, userId) => {
      dispatch(fetchfitRates({ styleNames, primarySize, userId }));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(FitRecommendation);
